import { useState, useCallback } from 'react';

interface SpeechMicTestResult {
  status: 'idle' | 'testing' | 'calibrating' | 'working' | 'muted' | 'error' | 'not_supported';
  error?: string;
  isChecking: boolean;
  confidence?: number;
  audioLevel?: number;
  adaptiveThreshold?: number;
  testPhase?: 'baseline' | 'speech_test' | 'complete';
}

export const useSpeechMicTest = () => {
  const [result, setResult] = useState<SpeechMicTestResult>({
    status: 'idle',
    isChecking: false,
    testPhase: 'baseline'
  });

  const testMicWithSpeech = useCallback(async (selectedAudioDeviceId?: string): Promise<boolean> => {
    setResult({ status: 'testing', isChecking: true, testPhase: 'baseline' });

    return new Promise(async (resolve) => {
      try {
        // Get direct microphone access for enhanced audio level testing
        const audioConstraints: MediaTrackConstraints = {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          ...(selectedAudioDeviceId && { deviceId: { exact: selectedAudioDeviceId } })
        };
        
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: audioConstraints
        });

        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        
        analyser.fftSize = 512;
        analyser.minDecibels = -100;
        analyser.maxDecibels = -30;
        source.connect(analyser);

        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        // Adaptive threshold approach
        let baselineNoise = 0;
        let measurementCount = 0;
        let adaptiveThreshold = 1; // Start with very low threshold
        let hasDetectedAudio = false;

        console.log('🎤 Starting enhanced microphone test with adaptive threshold...');

        const cleanup = () => {
          stream.getTracks().forEach(track => track.stop());
          audioContext.close().catch(console.error);
        };

        // Phase 1: Quick baseline measurement (0.5 seconds)
        const measureBaseline = () => {
          analyser.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((acc, value) => acc + value) / dataArray.length;
          
          baselineNoise = (baselineNoise * measurementCount + average) / (measurementCount + 1);
          measurementCount++;
          
          console.log(`📊 Baseline measurement ${measurementCount}/5: ${average.toFixed(2)}, Running avg: ${baselineNoise.toFixed(2)}`);

          if (measurementCount < 5) { // Reduced from 10 to 5 measurements
            setTimeout(measureBaseline, 100);
          } else {
            // Set adaptive threshold to 1.5x baseline noise + 0.5, minimum 0.5
            adaptiveThreshold = Math.max(0.5, baselineNoise * 1.5 + 0.5);
            console.log(`📊 Baseline complete: ${baselineNoise.toFixed(2)}, Adaptive threshold: ${adaptiveThreshold.toFixed(2)}`);
            
            setResult(prev => ({ 
              ...prev, 
              testPhase: 'speech_test', 
              adaptiveThreshold: adaptiveThreshold 
            }));
            
            startSpeechTest();
          }
        };

        // Phase 2: Speech-focused test - ask user to say "Hi, can you hear me?"
        const startSpeechTest = () => {
          console.log('🗣️ Starting speech test - user should say "Hi, can you hear me?"');
          
          let maxLevel = 0;
          let testDuration = 0;
          let sustainedAudioTime = 0;
          const testInterval = 100;
          const maxTestTime = 10000; // 10 seconds for speech test
          const speechThreshold = Math.max(0.3, adaptiveThreshold * 0.7); // Lower threshold for speech

          const detectSpeech = () => {
            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((acc, value) => acc + value) / dataArray.length;
            
            maxLevel = Math.max(maxLevel, average);
            setResult(prev => ({ ...prev, audioLevel: Math.round(average) }));
            
            console.log(`🗣️ Speech detection: ${average.toFixed(2)}, Max: ${maxLevel.toFixed(2)}, Threshold: ${speechThreshold.toFixed(2)}`);

            // Look for sustained audio activity (speech pattern)
            if (average > speechThreshold) {
              sustainedAudioTime += testInterval;
              
              // Require 500ms of sustained audio above threshold for speech
              if (sustainedAudioTime >= 500) {
                hasDetectedAudio = true;
                console.log('✅ Speech detected - microphone working');
                cleanup();
                setResult({ 
                  status: 'working', 
                  isChecking: false, 
                  testPhase: 'complete',
                  adaptiveThreshold: speechThreshold,
                  audioLevel: Math.round(average)
                });
                resolve(true);
                return;
              }
            } else {
              sustainedAudioTime = 0; // Reset if audio drops below threshold
            }
            
            testDuration += testInterval;

            if (testDuration >= maxTestTime) {
              cleanup();
              
              // Final check: if we detected ANY meaningful audio, consider it working
              if (maxLevel > 0.1) { // Very low threshold as last resort
                console.log('✅ Microphone working - some audio detected, assuming working mic with low volume');
                setResult({ 
                  status: 'working', 
                  isChecking: false, 
                  testPhase: 'complete',
                  adaptiveThreshold: 0.1,
                  audioLevel: Math.round(maxLevel)
                });
                resolve(true);
              } else {
                console.log('❌ No audio detected during speech test - microphone likely muted or disconnected');
                setResult({ 
                  status: 'muted', 
                  isChecking: false, 
                  testPhase: 'complete',
                  error: 'No audio detected when saying "Hi, can you hear me?" - microphone may be muted or disconnected',
                  adaptiveThreshold: speechThreshold
                });
                resolve(false);
              }
            } else {
              setTimeout(detectSpeech, testInterval);
            }
          };

          detectSpeech();
        };


        // Start with baseline measurement
        setTimeout(measureBaseline, 200); // Small delay to let audio context stabilize

        // Global safety timeout
        setTimeout(() => {
          if (result.isChecking) {
            cleanup();
            setResult({ 
              status: 'error', 
              isChecking: false, 
              testPhase: 'complete',
              error: 'Audio test timeout - please try again' 
            });
            resolve(false);
          }
        }, 12000); // 12 second absolute maximum (0.5s baseline + 10s speech + buffer)

      } catch (error) {
        console.error('Microphone access failed:', error);
        setResult({ 
          status: 'error', 
          isChecking: false, 
          testPhase: 'complete',
          error: 'Failed to access microphone - permission denied or device unavailable' 
        });
        resolve(false);
      }
    });
  }, []);

  const resetTest = useCallback(() => {
    setResult({ 
      status: 'idle', 
      isChecking: false, 
      testPhase: 'baseline',
      audioLevel: undefined,
      adaptiveThreshold: undefined
    });
  }, []);

  return { result, testMicWithSpeech, resetTest };
};